/* ==========================================================================
   BRUUN — thema-JavaScript
   Lades (menu, mandje, filters), AJAX-mandje met Section Rendering API,
   variantkeuze, galerij, voorspellend zoeken, filters en kleine details.
   Geen afhankelijkheden.
   ========================================================================== */
(() => {
  'use strict';

  const B = window.BRUUN || { routes: {}, strings: {} };
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* ---------- Geld ---------- */
  function formatMoney(cents, format = B.moneyFormat || '€{{amount_with_comma_separator}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const fmt = (n, decimals, thousands = '.', dec = ',') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + dec + frac : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount': return fmt(cents, 2, ',', '.');
        case 'amount_no_decimals': return fmt(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2, '.', ',');
      }
    });
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(html) {
    const el = $('#Toast');
    if (!el) return;
    el.innerHTML = html;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 320);
    }, 3800);
  }

  /* ---------- Header: hoogte en scroll-staat ---------- */
  function initHeader() {
    const wrap = $('.section-header');
    if (!wrap || wrap.dataset.ready) return;
    wrap.dataset.ready = '1';
    const setH = () => document.documentElement.style.setProperty('--header-h', wrap.offsetHeight + 'px');
    setH();
    window.addEventListener('resize', debounce(setH, 150));
    const onScroll = () => wrap.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Uitklapmenu's: op touch eerste tik opent, tweede tik volgt de link
    $$('[data-catbar-toggle]').forEach((link) => {
      const item = link.closest('[data-catbar-item]');
      link.addEventListener('click', (e) => {
        const coarse = window.matchMedia('(hover: none)').matches;
        if (coarse && !item.classList.contains('is-open')) {
          e.preventDefault();
          $$('[data-catbar-item].is-open').forEach((i) => { i.classList.remove('is-open'); $('[data-catbar-toggle]', i)?.setAttribute('aria-expanded', 'false'); });
          item.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
        }
      });
      item.addEventListener('mouseenter', () => link.setAttribute('aria-expanded', 'true'));
      item.addEventListener('mouseleave', () => { link.setAttribute('aria-expanded', 'false'); item.classList.remove('is-open'); });
      item.addEventListener('focusin', () => link.setAttribute('aria-expanded', 'true'));
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) link.setAttribute('aria-expanded', 'false'); });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { item.classList.remove('is-open'); link.setAttribute('aria-expanded', 'false'); link.focus(); }
      });
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-catbar-item]')) $$('[data-catbar-item].is-open').forEach((i) => i.classList.remove('is-open'));
    });
  }

  /* ---------- USP-balk: op mobiel wisselen ---------- */
  function initUsp() {
    $$('[data-usp-rotator]').forEach((list) => {
      const items = $$('.usp-bar__item', list);
      if (items.length < 2 || list.dataset.ready) return;
      list.dataset.ready = '1';
      let i = 0;
      let paused = false;
      list.addEventListener('mouseenter', () => { paused = true; });
      list.addEventListener('mouseleave', () => { paused = false; });
      list.addEventListener('focusin', () => { paused = true; });
      list.addEventListener('focusout', () => { paused = false; });
      const mq = window.matchMedia('(max-width: 749px)');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) return;
      setInterval(() => {
        if (!mq.matches || paused) return;
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4200);
    });
  }

  /* ---------- Lades ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
  const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  async function readJson(res) {
    try { return await res.json(); } catch (e) { return {}; }
  }
  let lastFocus = null;

  function openDrawer(drawer, opener) {
    if (!drawer) return;
    lastFocus = opener || document.activeElement;
    drawer.classList.add('is-open', 'is-entering');
    clearTimeout(drawer._enterTimer);
    drawer._enterTimer = setTimeout(() => drawer.classList.remove('is-entering'), 1100);
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const panel = $('.drawer__panel', drawer);
    setTimeout(() => (panel || drawer).focus({ preventScroll: true }), 40);
  }
  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    if (!$('.drawer.is-open')) document.body.classList.remove('is-locked');
    $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  function initDrawers() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        openDrawer(document.getElementById(opener.dataset.drawerOpen), opener);
        return;
      }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        e.preventDefault();
        closeDrawer(closer.closest('[data-drawer]'));
      }
    });
    document.addEventListener('keydown', (e) => {
      const open = $$('.drawer.is-open').pop();
      if (!open) return;
      if (e.key === 'Escape') { closeDrawer(open); return; }
      if (e.key === 'Tab') {
        const nodes = $$(FOCUSABLE, open).filter((n) => n.offsetParent !== null);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const inside = nodes.includes(document.activeElement);
        if (!inside) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---------- Mandje ---------- */
  const Cart = {
    drawer() { return $('#CartDrawer'); },
    sectionIds() {
      const ids = ['cart-drawer'];
      const main = $('[data-main-cart]');
      if (main) {
        const wrap = main.closest('.shopify-section');
        if (wrap) ids.push(wrap.id.replace('shopify-section-', ''));
      }
      return ids;
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
      $$('[data-cart-count-label]').forEach((el) => {
        const tpl = Number(count) === 1 ? el.dataset.one : el.dataset.other;
        if (tpl) el.textContent = tpl.replace('[count]', count);
      });
    },
    render(sections) {
      if (!sections) return;
      const parser = new DOMParser();
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parser.parseFromString(html, 'text/html');
        if (id === 'cart-drawer') {
          const fresh = $('.drawer__panel', doc);
          const current = $('#CartDrawer .drawer__panel');
          if (fresh && current) {
            const scroll = $('.drawer__body', current)?.scrollTop || 0;
            const active = document.activeElement;
            let focusKey = null;
            if (active && current.contains(active)) {
              const line = active.closest('[data-line]');
              focusKey = line ? { line: line.dataset.line, sel: active.matches('[data-qty-plus]') ? '[data-qty-plus]' : active.matches('[data-qty-minus]') ? '[data-qty-minus]' : active.matches('[data-line-qty]') ? '[data-line-qty]' : null } : null;
            }
            current.innerHTML = fresh.innerHTML;
            current.dataset.count = fresh.dataset.count;
            const body = $('.drawer__body', current);
            if (body) body.scrollTop = scroll;
            this.updateCount(fresh.dataset.count);
            if (focusKey) {
              const target = focusKey.sel ? $(`[data-line="${focusKey.line}"] ${focusKey.sel}`, current) : null;
              (target || current).focus({ preventScroll: true });
            }
          }
        } else {
          const target = document.getElementById(`shopify-section-${id}`);
          const fresh = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
          if (target && fresh) target.innerHTML = fresh.innerHTML;
        }
      });
    },
    async add(formData, button) {
      button?.classList.add('is-loading');
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${B.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await readJson(res);
        if (!res.ok || data.status) throw new Error(data.description || data.message || B.strings.error);
        if (B.cartType === 'page') {
          window.location.href = B.routes.cart;
          return;
        }
        this.render(data.sections);
        this.markNew([formData.get('id')], button);
        openDrawer(this.drawer(), button);
      } catch (err) {
        const msg = err && err.message && !/fetch|JSON|token/i.test(err.message) ? err.message : B.strings.error;
        toast(`<span>${escapeHtml(msg)}</span>`);
      } finally {
        button?.classList.remove('is-loading');
      }
    },
    markNew(ids, button) {
      ids.filter(Boolean).forEach((id) => {
        const line = $(`#CartDrawer [data-variant="${String(id).replace(/[^\w-]/g, '')}"]`);
        if (line) { line.classList.remove('is-new'); void line.offsetWidth; line.classList.add('is-new'); }
      });
      if (button && (button.classList.contains('qbtn') || button.classList.contains('mini__add') || button.classList.contains('herox__add-btn'))) {
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1400);
      }
    },
    async addItems(items, button) {
      button?.classList.add('is-loading');
      try {
        const res = await fetch(`${B.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: this.sectionIds(), sections_url: window.location.pathname })
        });
        const data = await readJson(res);
        if (!res.ok || data.status) throw new Error(data.description || data.message || B.strings.error);
        if (B.cartType === 'page') { window.location.href = B.routes.cart; return; }
        this.render(data.sections);
        this.markNew(items.map((it) => it.id));
        openDrawer(this.drawer(), button);
      } catch (err) {
        const msg = err && err.message && !/fetch|JSON|token/i.test(err.message) ? err.message : B.strings.error;
        toast(`<span>${escapeHtml(msg)}</span>`);
      } finally {
        button?.classList.remove('is-loading');
      }
    },
    async change(line, quantity) {
      const panel = $('#CartDrawer .drawer__panel');
      panel?.setAttribute('aria-busy', 'true');
      $$('[data-main-cart]').forEach((m) => m.setAttribute('aria-busy', 'true'));
      try {
        const res = await fetch(`${B.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
        });
        const data = await readJson(res);
        if (!res.ok || data.status) throw new Error(data.description || data.message || B.strings.error);
        this.render(data.sections);
      } catch (err) {
        const msg = err && err.message && !/fetch|JSON|token/i.test(err.message) ? err.message : B.strings.error;
        toast(`<span>${escapeHtml(msg)}</span>`);
      } finally {
        panel?.removeAttribute('aria-busy');
        $$('[data-main-cart]').forEach((m) => m.removeAttribute('aria-busy'));
      }
    },
    async note(value) {
      try {
        await fetch(`${B.routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value })
        });
      } catch (e) { /* stil */ }
    }
  };

  function initCart() {
    // Product toevoegen (productkaart en productpagina)
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      if (B.cartType === 'page' && !window.fetch) return;
      e.preventDefault();
      const button = e.submitter || $('[type="submit"]', form);
      if (button?.disabled) return;
      Cart.add(new FormData(form), button);
    });

    // Mandje openen
    document.addEventListener('click', (e) => {
      const open = e.target.closest('[data-cart-open]');
      if (open && B.cartType !== 'page' && Cart.drawer() && !document.body.classList.contains('template-cart')) {
        e.preventDefault();
        openDrawer(Cart.drawer(), open);
      }
      const remove = e.target.closest('[data-line-remove]');
      if (remove) {
        e.preventDefault();
        Cart.change(Number(remove.dataset.lineRemove), 0);
      }
    });

    // Aantallen in het mandje: één wachtrij, één timer per regel
    const timers = {};
    let queue = Promise.resolve();
    const changeQty = (input) => {
      const line = Number(input.dataset.lineQty);
      clearTimeout(timers[line]);
      timers[line] = setTimeout(() => {
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        queue = queue.then(() => Cart.change(line, qty));
      }, 350);
    };
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (input) changeQty(input);
      const note = e.target.closest('[data-cart-note]');
      if (note) Cart.note(note.value);
    });
  }

  /* ---------- Aantal-knoppen (overal) ---------- */
  function initQty() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
      if (!btn) return;
      const wrap = btn.closest('[data-qty]');
      const input = $('input', wrap);
      if (!input) return;
      const min = input.hasAttribute('min') ? Number(input.min) : 1;
      const max = input.hasAttribute('max') && input.max !== '' ? Number(input.max) : Infinity;
      let val = parseInt(input.value, 10) || 0;
      val = btn.hasAttribute('data-qty-plus') ? val + 1 : val - 1;
      val = Math.min(max, Math.max(min, val));
      if (String(val) === input.value) return;
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Productpagina ---------- */
  function initProduct(root = document) {
    $$('[data-product]', root).forEach((product) => {
      if (product.dataset.ready) return;
      product.dataset.ready = '1';
      const jsonEl = $('[data-variants]', product);
      if (!jsonEl) return;
      let variants = [];
      try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
      const form = $('form[data-product-form]', product);
      const idInput = form ? $('input[name="id"]', form) : null;
      const addBtn = form ? $('[data-add-button]', form) : null;
      const priceWrap = $('[data-product-price]', product);
      const priceEl = priceWrap ? $('.price', priceWrap) : null;
      const stockEl = $('[data-product-stock]', product);
      const picker = $('[data-variant-picker]', product);
      const stickyPrice = $('[data-sticky-price]');
      const stickyBtn = $('[data-sticky-add]');
      const lowThreshold = Number(product.dataset.lowStock || 5);
      const isB2B = product.dataset.b2b === 'ja';
      const isBulk = product.dataset.bulk === 'ja';
      const askEl = $('[data-b2b-ask]', product);
      const S = B.strings;

      const currentOptions = () => $$('fieldset[data-option]', picker || product).map((fs) => {
        const checked = $('input:checked', fs);
        const select = $('select', fs);
        return checked ? checked.value : select ? select.value : null;
      });

      const update = (variant) => {
        if (!variant) {
          if (addBtn) { addBtn.disabled = true; addBtn.textContent = B.strings.unavailable; }
          if (stickyBtn) stickyBtn.disabled = true;
          return;
        }
        if (idInput) idInput.value = variant.id;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        const ask = variant.ask;
        if (priceEl) {
          if (isB2B) {
            let rrp = '';
            if (!isBulk && S.rrp && variant.rrp > 0) {
              const advEx = variant.rrp * 100 / 121;
              const margin = advEx > 0 ? Math.round((advEx - variant.price) * 100 / advEx) : 0;
              rrp = `<span class="price__rrp">${S.rrp.replace('[price]', formatMoney(variant.rrp))}${S.margin && margin > 0 ? ' · ' + S.margin.replace('[pct]', margin) : ''}</span>`;
            }
            priceEl.innerHTML = ask
              ? `<span class="price__now price__ask">${escapeHtml(S.onRequest)}</span>`
              : `<span class="price__now">${formatMoney(variant.price)}</span><span class="price__tax">${escapeHtml(S.exVat)}</span>${rrp}`;
          } else {
            priceEl.classList.remove('price--sale');
            priceEl.innerHTML = (ask
              ? `<span class="price__now price__ask">${escapeHtml(S.onRequest)}</span>`
              : `<span class="price__now">${formatMoney(variant.rrp)}</span><span class="price__tax">${escapeHtml(S.advLabel)}</span>`) +
              (S.loginPrice ? `<span class="price__rrp"><a href="${B.routes.account}">${escapeHtml(S.loginPrice)}</a></span>` : '');
          }
        }
        if (stickyPrice) stickyPrice.textContent = ask ? S.onRequest : isB2B ? formatMoney(variant.price) + ' ' + S.exShort : formatMoney(variant.rrp);
        const buyable = variant.available && !ask;
        const label = ask ? S.onRequest : variant.available ? S.addToCart : S.soldOut;
        if (addBtn) {
          addBtn.disabled = !buyable;
          addBtn.textContent = label;
        }
        if (stickyBtn) {
          stickyBtn.disabled = !buyable;
          stickyBtn.textContent = label;
        }
        if (askEl) askEl.hidden = !ask;
        if (stockEl) {
          stockEl.classList.remove('stock--in', 'stock--low', 'stock--out');
          if (!variant.available) { stockEl.classList.add('stock--out'); stockEl.textContent = B.strings.outOfStock; }
          else if (variant.inventory !== null && variant.inventory <= lowThreshold) { stockEl.classList.add('stock--low'); stockEl.textContent = B.strings.lowStock.replace('[count]', variant.inventory); }
          else { stockEl.classList.add('stock--in'); stockEl.textContent = B.strings.inStock; }
        }
        // beschikbaarheid per optiewaarde
        if (picker) {
          const sel = currentOptions();
          $$('fieldset[data-option]', picker).forEach((fs, idx) => {
            $$('input', fs).forEach((input) => {
              const test = sel.slice(); test[idx] = input.value;
              const match = variants.find((v) => v.options.every((o, i) => o === test[i]));
              input.classList.toggle('is-unavailable', !match || !match.available);
            });
          });
        }
        if (variant.media_id) {
          const slide = $(`[data-media-id="${variant.media_id}"]`, product);
          if (slide) product.dispatchEvent(new CustomEvent('gallery:go', { detail: { slide } }));
        }
      };

      if (picker) {
        picker.addEventListener('change', () => {
          const sel = currentOptions();
          update(variants.find((v) => v.options.every((o, i) => o === sel[i])));
        });
        update(variants.find((v) => String(v.id) === String(idInput?.value)) || variants[0]);
      }

      // Sticky knop op mobiel
      const sticky = $('[data-sticky-atc]');
      if (sticky && addBtn && 'IntersectionObserver' in window) {
        sticky.inert = true;
        const io = new IntersectionObserver(([entry]) => {
          const below = entry.boundingClientRect.top < 0;
          const show = !entry.isIntersecting && below;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', String(!show));
          sticky.inert = !show;
        });
        io.observe(addBtn);
        stickyBtn?.addEventListener('click', () => {
          if (form.requestSubmit) form.requestSubmit(addBtn); else addBtn.click();
        });
      }

      initGallery(product);
    });
  }

  function initGallery(product) {
    const slides = $('[data-gallery-slides]', product);
    if (!slides) return;
    const items = $$('[data-gallery-slide]', slides);
    const thumbs = $$('[data-gallery-thumb]', product);
    const dots = $$('[data-gallery-dot]', product);
    const counter = $('[data-gallery-index]', product);
    const setActive = (idx) => {
      thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === idx)));
      dots.forEach((d, i) => d.setAttribute('aria-current', String(i === idx)));
      if (counter) counter.textContent = String(idx + 1);
    };
    const go = (idx) => {
      const target = items[idx];
      if (!target) return;
      slides.scrollTo({ left: target.offsetLeft - slides.offsetLeft, behavior: 'smooth' });
      setActive(idx);
    };
    thumbs.forEach((t, i) => t.addEventListener('click', () => go(i)));
    dots.forEach((d, i) => d.addEventListener('click', () => go(i)));
    slides.addEventListener('scroll', debounce(() => {
      const idx = Math.round(slides.scrollLeft / slides.clientWidth);
      setActive(idx);
    }, 60), { passive: true });
    product.addEventListener('gallery:go', (e) => go(items.indexOf(e.detail.slide)));
  }

  /* ---------- Voorspellend zoeken ---------- */
  function initPredictive() {
    if (!B.predictiveSearch || !B.routes.predictiveSearch) return;
    $$('[data-predictive-search]').forEach((wrap) => {
      const input = $('[data-predictive-input]', wrap);
      const results = $('[data-predictive-results]', wrap);
      if (!input || !results || wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      let controller;
      const close = () => { results.hidden = true; };
      const search = debounce(async () => {
        const q = input.value.trim();
        controller?.abort();
        if (q.length < 2) { close(); return; }
        controller = new AbortController();
        try {
          const url = `${B.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error();
          const html = await res.text();
          if (input.value.trim() !== q) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = $('[data-predictive-inner]', doc);
          results.innerHTML = inner ? inner.innerHTML : '';
          results.hidden = !inner;
        } catch (e) { /* afgebroken of offline */ }
      }, 220);
      input.addEventListener('input', search);
      input.addEventListener('focus', () => { if (results.innerHTML.trim() && input.value.trim().length > 1) results.hidden = false; });
      input.addEventListener('keydown', (e) => {
        const links = $$('a, button', results);
        if (!links.length || results.hidden) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); links[0].focus(); }
        if (e.key === 'Escape') close();
      });
      results.addEventListener('keydown', (e) => {
        const links = $$('a, button', results);
        const idx = links.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); (links[idx + 1] || links[0]).focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); idx <= 0 ? input.focus() : links[idx - 1].focus(); }
        if (e.key === 'Escape') { close(); input.focus(); }
      });
      document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
    });
  }

  /* ---------- Filters en sortering ---------- */
  function initFacets() {
    $$('form[data-facets-form]').forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = '1';
      const submit = () => {
        const data = new FormData(form);
        const params = new URLSearchParams();
        for (const [k, v] of data.entries()) { if (v !== '') params.append(k, v); }
        window.location.search = params.toString();
      };
      form.addEventListener('change', (e) => {
        if (e.target.matches('[data-price-input]')) return;
        if (form.hasAttribute('data-facets-instant')) submit();
      });
      form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
    });
    // sorteren
    $$('[data-sort-select]').forEach((select) => {
      if (select.dataset.ready) return;
      select.dataset.ready = '1';
      select.addEventListener('change', () => {
        const form = $('#Facets');
        if (form) {
          const hidden = $('input[name="sort_by"]', form);
          if (hidden) hidden.value = select.value;
          form.dispatchEvent(new Event('submit', { cancelable: true }));
          return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
    if (document.documentElement.dataset.facetsReady) return;
    document.documentElement.dataset.facetsReady = '1';
    // filters sluiten bij klik erbuiten
    document.addEventListener('click', (e) => {
      $$('details.facet[open]').forEach((d) => { if (!d.contains(e.target) && !d.closest('.mobile-facets')) d.removeAttribute('open'); });
    });
    $$('details.facet').forEach((d) => d.addEventListener('toggle', () => {
      if (d.open && !d.closest('.mobile-facets')) $$('details.facet[open]').forEach((o) => { if (o !== d && !o.closest('.mobile-facets')) o.removeAttribute('open'); });
    }));
  }

  /* ---------- Aanbevelingen (Shopify) ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (section) => {
      const url = section.dataset.url;
      if (!url || section.dataset.ready) return;
      section.dataset.ready = '1';
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const list = $('[data-recommendations-list]', doc);
        if (list && list.children.length) $('[data-recommendations-list]', section).innerHTML = list.innerHTML;
        if (!$('[data-recommendations-list]', section).children.length) section.hidden = true;
      } catch (e) { /* laat de collectie-fallback staan */ }
    });
  }

  /* ---------- Rustig binnenkomen ---------- */
  function initReveal() {
    const els = $$('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  }


  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || !document.body.classList.contains('motion-on');

  /* ---------- Hero: kleurpodium, één snack per dier ---------- */
  function initHerox() {
    $$('[data-herox]').forEach((hero) => {
      if (hero.dataset.ready) return;
      hero.dataset.ready = '1';
      const stage = $('[data-herox-stage]', hero);
      const slides = $$('[data-herox-slide]', hero);
      const segs = $$('[data-herox-seg]', hero);
      if (!stage || !slides.length) return;
      const interval = Number(hero.dataset.interval) || 4000;
      hero.style.setProperty('--dur', `${interval}ms`);
      let index = 0;
      let timer = null;
      let busy = false;
      const color = (slide) => `var(--${slide.dataset.dier || 'kip'})`;
      stage.style.setProperty('--stage', color(slides[0]));
      const focusables = (slide, on) => $$('a, button', slide).forEach((el) => { el.tabIndex = on ? 0 : -1; });
      const setSegs = () => segs.forEach((seg, i) => {
        if (i === index) seg.setAttribute('aria-current', 'true'); else seg.removeAttribute('aria-current');
        seg.classList.toggle('is-done', i < index);
        const bar = $('span', seg);
        if (bar && i === index) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
      });
      const show = (n) => {
        if (busy || n === index) return;
        const next = (n + slides.length) % slides.length;
        const prev = slides[index];
        const incoming = slides[next];
        busy = true;
        index = next;
        prev.classList.remove('is-active');
        prev.classList.add('is-leaving');
        prev.setAttribute('aria-hidden', 'true');
        focusables(prev, false);
        incoming.classList.add('is-active');
        incoming.removeAttribute('aria-hidden');
        focusables(incoming, true);
        setSegs();
        if (reduceMotion()) {
          stage.style.setProperty('--stage', color(incoming));
          prev.classList.remove('is-leaving');
          busy = false;
          return;
        }
        stage.style.setProperty('--wipe', color(incoming));
        stage.classList.remove('is-wiping');
        void stage.offsetWidth;
        stage.classList.add('is-wiping');
        setTimeout(() => {
          stage.style.setProperty('--stage', color(incoming));
          stage.classList.remove('is-wiping');
          prev.classList.remove('is-leaving');
          busy = false;
        }, 780);
      };
      const stop = () => { clearInterval(timer); timer = null; hero.classList.remove('is-playing'); };
      const play = () => {
        stop();
        if (reduceMotion() || slides.length < 2) return;
        hero.classList.add('is-playing');
        setSegs();
        timer = setInterval(() => show(index + 1), interval);
      };
      slides.forEach((sl, i) => focusables(sl, i === 0));
      segs.forEach((seg, i) => seg.addEventListener('click', () => { show(i); play(); }));
      stage.addEventListener('mouseenter', () => { hero.classList.add('is-paused'); stop(); hero.classList.add('is-playing'); });
      stage.addEventListener('mouseleave', () => { hero.classList.remove('is-paused'); play(); });
      stage.addEventListener('focusin', () => { hero.classList.add('is-paused'); stop(); });
      stage.addEventListener('focusout', () => { hero.classList.remove('is-paused'); play(); });
      // een beetje diepte als je de muis beweegt
      if (window.matchMedia('(pointer: fine)').matches && !reduceMotion()) {
        stage.addEventListener('pointermove', (e) => {
          const r = stage.getBoundingClientRect();
          stage.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
          stage.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
        });
        stage.addEventListener('pointerleave', () => { stage.style.setProperty('--mx', 0); stage.style.setProperty('--my', 0); });
      }
      let startX = null;
      stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) { show(index + (dx < 0 ? 1 : -1)); play(); }
        startX = null;
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => entries.forEach((en) => (en.isIntersecting ? play() : stop()))).observe(stage);
      } else play();
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
    });
  }

  /* ---------- Per gebruik: foto's wisselen bij hover of in beeld ---------- */
  function initUtiles() {
    $$('[data-utile]').forEach((tile) => {
      if (tile.dataset.ready) return;
      tile.dataset.ready = '1';
      const photos = $$('[data-utile-photo]', tile);
      if (photos.length < 2) return;
      let i = 0;
      let timer = null;
      const step = () => {
        photos[i].classList.remove('is-active');
        i = (i + 1) % photos.length;
        photos[i].classList.add('is-active');
      };
      const start = (delay = 900) => { if (reduceMotion() || timer) return; step(); timer = setInterval(step, delay); };
      const stop = () => { clearInterval(timer); timer = null; };
      if (window.matchMedia('(hover: hover)').matches) {
        tile.addEventListener('mouseenter', () => start());
        tile.addEventListener('mouseleave', stop);
      } else if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => entries.forEach((en) => (en.intersectionRatio > 0.6 ? start(1800) : stop())), { threshold: [0, 0.6] }).observe(tile);
      }
    });
  }

  /* ---------- Kies een dier ---------- */
  function initPicker() {
    $$('[data-picker]').forEach((picker) => {
      if (picker.dataset.ready) return;
      picker.dataset.ready = '1';
      const items = $$('[data-picker-item]', picker);
      const open = (item) => {
        items.forEach((it) => {
          const on = it === item;
          it.classList.toggle('is-open', on);
          const tab = $('[data-picker-tab]', it);
          const panel = $('[data-picker-panel]', it);
          if (tab) tab.setAttribute('aria-expanded', String(on));
          if (panel) panel.hidden = !on;
        });
      };
      let hoverTimer = null;
      items.forEach((item, idx) => {
        const tab = $('[data-picker-tab]', item);
        if (!tab) return;
        tab.addEventListener('click', () => open(item));
        tab.addEventListener('keydown', (e) => {
          if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
          e.preventDefault();
          const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
          const next = items[(idx + dir + items.length) % items.length];
          open(next);
          $('[data-picker-tab]', next)?.focus();
        });
        if (window.matchMedia('(hover: hover) and (min-width: 850px)').matches) {
          item.addEventListener('mouseenter', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => open(item), 140); });
          item.addEventListener('mouseleave', () => clearTimeout(hoverTimer));
        }
      });
    });
  }

  /* ---------- Wat zit erin: grafiek die herschikt ---------- */
  function initChart() {
    $$('[data-chart]').forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const chart = $('.chart', root) || root;
      const list = $('[data-chart-rows]', root);
      if (!list) return;
      const rows = $$('[data-chart-row]', list);
      const visible = Number(root.dataset.visible) || 10;
      const more = $('[data-chart-more]', root);
      let showAll = false;
      let metric = 'eiwit';
      const fmt = (n) => String(Math.round(n * 100) / 100).replace('.', ',');
      const apply = (animate) => {
        const before = new Map(rows.map((r) => [r, r.getBoundingClientRect().top]));
        const sorted = rows.slice().sort((a, b) => Number(b.dataset[metric]) - Number(a.dataset[metric]));
        sorted.forEach((r, i) => {
          list.appendChild(r);
          const v = Number(r.dataset[metric]) || 0;
          r.style.setProperty('--v', `${Math.min(100, v)}%`);
          const val = $('[data-chart-val]', r);
          if (val) val.textContent = `${fmt(v)}%`;
          r.classList.toggle('is-hidden', !showAll && i >= visible);
        });
        if (more) more.hidden = showAll || rows.length <= visible;
        if (!animate || reduceMotion()) return;
        sorted.forEach((r) => {
          if (r.classList.contains('is-hidden')) return;
          const prev = before.get(r);
          const now = r.getBoundingClientRect().top;
          if (prev == null || Math.abs(prev - now) < 1) return;
          r.animate([{ transform: `translateY(${prev - now}px)` }, { transform: 'none' }], { duration: 600, easing: 'cubic-bezier(.2,.7,.2,1)' });
        });
      };
      $$('[data-chart-metric]', root).forEach((btn) => btn.addEventListener('click', () => {
        metric = btn.dataset.chartMetric;
        $$('[data-chart-metric]', root).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        apply(true);
      }));
      more?.addEventListener('click', () => { showAll = true; apply(false); });
      apply(false);
      if (!('IntersectionObserver' in window) || reduceMotion()) { chart.classList.add('is-in'); return; }
      const io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (en.isIntersecting) { chart.classList.add('is-in'); io.disconnect(); }
      }), { threshold: 0.2 });
      io.observe(list);
    });
  }

  /* ---------- Tellers ---------- */
  function initCounters() {
    const els = $$('[data-count-to]');
    if (!els.length || !('IntersectionObserver' in window) || reduceMotion()) return;
    const run = (el) => {
      const target = parseFloat(String(el.dataset.countTo).replace(',', '.'));
      if (!isFinite(target) || target <= 1) return;
      const decimals = Number(el.dataset.decimals || 0);
      const dur = 1100;
      const t0 = performance.now();
      const fmt = (v) => v.toFixed(decimals).replace('.', ',');
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt(target * eased);
        if (p < 1) requestAnimationFrame(step); else el.textContent = fmt(target);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { io.unobserve(e.target); run(e.target); } });
    }, { threshold: 0.4 });
    els.forEach((el) => { if (!el.dataset.ready) { el.dataset.ready = '1'; io.observe(el); } });
  }

  /* ---------- Tabs ---------- */
  function initTabs() {
    $$('[data-tabs]').forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const tabs = $$('[role="tab"]', root);
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); select(tabs[(i + 1) % tabs.length], true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); select(tabs[(i - 1 + tabs.length) % tabs.length], true); }
        });
      });
    });
  }

  /* ---------- Taal- en landkiezer ---------- */
  function initLocalization() {
    if (document.documentElement.dataset.locReady) return;
    document.documentElement.dataset.locReady = '1';
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-loc-toggle]');
      $$('[data-loc-toggle][aria-expanded="true"]').forEach((t) => {
        if (t !== toggle) { t.setAttribute('aria-expanded', 'false'); const l = document.getElementById(t.getAttribute('aria-controls')); if (l) l.hidden = true; }
      });
      if (!toggle) return;
      const list = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      if (list) { list.hidden = !open; if (open) $('[aria-current="true"], button', list)?.focus(); }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const t = $('[data-loc-toggle][aria-expanded="true"]');
      if (t) { t.setAttribute('aria-expanded', 'false'); document.getElementById(t.getAttribute('aria-controls')).hidden = true; t.focus(); }
    });
  }

  /* ---------- Bundel ---------- */
  function initBundles() {
    $$('[data-bundle]').forEach((bundle) => {
      if (bundle.dataset.ready) return;
      bundle.dataset.ready = '1';
      const total = $('[data-bundle-total]', bundle);
      const btn = $('[data-bundle-add]', bundle);
      const checks = () => $$('[data-bundle-check]', bundle);
      const recalc = () => {
        const sum = checks().filter((c) => c.checked).reduce((a, c) => a + Number(c.dataset.price || 0), 0);
        if (total) total.textContent = formatMoney(sum);
        if (btn) btn.disabled = checks().filter((c) => c.checked && !c.disabled).length === 0 && !$('[data-bundle-main]', bundle);
      };
      bundle.addEventListener('change', recalc);
      // hoofdproduct volgt de gekozen variant
      const product = bundle.closest('[data-product]');
      const main = $('[data-bundle-main]', bundle);
      if (product && main) {
        const idInput = $('form[data-product-form] input[name="id"]', product);
        const sync = () => {
          if (!idInput) return;
          main.value = idInput.value;
          try {
            const variants = JSON.parse($('[data-variants]', product).textContent);
            const v = variants.find((x) => String(x.id) === String(idInput.value));
            if (v) main.dataset.price = v.price;
          } catch (e) { /* laat staan */ }
          recalc();
        };
        product.addEventListener('change', () => setTimeout(sync, 0));
      }
      btn?.addEventListener('click', () => {
        const items = checks().filter((c) => c.checked).map((c) => ({ id: Number(c.value), quantity: 1 }));
        if (items.length) Cart.addItems(items, btn);
      });
      recalc();
    });
  }

  /* ---------- Etiket uitgelegd ---------- */
  function initExplainer() {
    $$('[data-explainer]').forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const items = $$('[data-point]', root);
      let auto = null;
      let current = -1;
      const light = (region) => {
        items.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.point === region)));
        $$('[data-region]', root).forEach((el) => el.classList.toggle('is-lit', el.dataset.region === region));
        $$('[data-pin]', root).forEach((el) => el.classList.toggle('is-lit', el.dataset.pin === region));
      };
      const stopAuto = () => { if (auto) clearInterval(auto); auto = null; };
      items.forEach((b, i) => {
        b.addEventListener('mouseenter', () => { stopAuto(); light(b.dataset.point); });
        b.addEventListener('focus', () => { stopAuto(); light(b.dataset.point); });
        b.addEventListener('click', () => { stopAuto(); light(b.dataset.point); current = i; });
      });
      $$('[data-pin]', root).forEach((pin) => pin.addEventListener('click', () => { stopAuto(); light(pin.dataset.pin); }));
      if (!reduceMotion() && 'IntersectionObserver' in window && items.length) {
        const io = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !auto && current === -1) {
            let i = 0; light(items[0].dataset.point);
            auto = setInterval(() => { i = (i + 1) % items.length; light(items[i].dataset.point); }, 2600);
          } else if (!entry.isIntersecting) stopAuto();
        }, { threshold: 0.35 });
        io.observe(root);
      } else if (items.length) light(items[0].dataset.point);
    });
  }

  /* ---------- FAQ zoeken ---------- */
  function initFaq() {
    $$('[data-faq]').forEach((root) => {
      const input = $('[data-faq-search]', root);
      if (!input || input.dataset.ready) return;
      input.dataset.ready = '1';
      const items = $$('[data-faq-item]', root);
      const empty = $('[data-faq-empty]', root);
      const cats = $$('.faq__cat-title', root);
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        let hits = 0;
        items.forEach((d) => {
          const match = !q || d.textContent.toLowerCase().includes(q);
          d.hidden = !match;
          if (match) hits++;
          if (q && match) d.open = true;
        });
        cats.forEach((c) => { c.hidden = !!q; });
        if (empty) empty.hidden = hits > 0;
      });
    });
  }

  /* ---------- Galerij: echte kleur en vergroten ---------- */
  function initGalleryTools() {
    $$('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.toolsReady) return;
      gallery.dataset.toolsReady = '1';
      const product = gallery.closest('[data-product]') || document;
      const toggle = $('[data-true-color]', gallery);
      toggle?.addEventListener('click', () => {
        const on = toggle.getAttribute('aria-pressed') !== 'true';
        toggle.setAttribute('aria-pressed', String(on));
        gallery.classList.toggle('is-true-color', on);
        $('[data-lightbox]', product)?.classList.toggle('is-true-color', on);
      });
      const dialog = $('[data-lightbox]', product);
      const img = dialog ? $('[data-lightbox-img]', dialog) : null;
      const count = dialog ? $('[data-lightbox-count]', dialog) : null;
      const slides = $$('[data-gallery-slide][data-full]', gallery);
      if (!dialog || !img || !slides.length) return;
      let i = 0;
      const show = (n) => {
        i = (n + slides.length) % slides.length;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('loading');
        img.src = slides[i].dataset.full;
        img.alt = slides[i].dataset.alt || '';
        if (count) count.textContent = `${i + 1} / ${slides.length}`;
      };
      const current = () => {
        const track = $('[data-gallery-slides]', gallery);
        return track ? Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) : 0;
      };
      const open = () => { show(current()); if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', ''); };
      $('[data-lightbox-open]', gallery)?.addEventListener('click', open);
      slides.forEach((s) => s.addEventListener('click', (e) => { if (!e.target.closest('button')) open(); }));
      $('[data-lightbox-close]', dialog)?.addEventListener('click', () => dialog.close());
      $('[data-lightbox-prev]', dialog)?.addEventListener('click', () => show(i - 1));
      $('[data-lightbox-next]', dialog)?.addEventListener('click', () => show(i + 1));
      dialog.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') show(i + 1); if (e.key === 'ArrowLeft') show(i - 1); });
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    });
  }

  /* ---------- Navigatie: strook bovenaan en paginaovergang ---------- */
  function initNavMotion() {
    if (document.documentElement.dataset.navReady) return;
    document.documentElement.dataset.navReady = '1';
    let bar = $('.nav-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'nav-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<div class="strook">' + '<span></span>'.repeat(13) + '</div>';
      document.body.appendChild(bar);
    }
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (a.target && a.target !== '_self') return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin || (url.pathname === location.pathname && url.hash)) return;
      if (a.hasAttribute('download') || a.getAttribute('href').startsWith('#')) return;
      // de zak van de aangeklikte kaart vloeit over naar de productpagina
      const card = a.closest('[data-card]');
      if (card) {
        $$('.photo[style*="view-transition-name"]').forEach((p) => { p.style.viewTransitionName = ''; });
        const photo = $('.photo', card);
        if (photo) photo.style.viewTransitionName = 'bruun-photo';
      }
      if (!reduceMotion()) { bar.classList.remove('is-active'); void bar.offsetWidth; bar.classList.add('is-active'); }
    });
    window.addEventListener('pageshow', () => bar.classList.remove('is-active'));
  }

  /* ---------- Adressen (klantaccount) ---------- */
  function initAddresses() {
    $$('[data-address-country]').forEach((country) => {
      if (country.dataset.ready) return;
      country.dataset.ready = '1';
      const province = document.getElementById(country.dataset.provinceSelect);
      const provinceWrap = province ? province.closest('[data-province-wrap]') : null;
      if (country.dataset.default) country.value = country.dataset.default;
      const fill = () => {
        if (!province) return;
        const opt = country.options[country.selectedIndex];
        let list = [];
        try { list = JSON.parse(opt?.dataset.provinces || '[]'); } catch (e) { list = []; }
        province.innerHTML = list.map(([value, label]) => `<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`).join('');
        if (provinceWrap) provinceWrap.hidden = list.length === 0;
        if (province.dataset.default) province.value = province.dataset.default;
      };
      country.addEventListener('change', () => { if (province) province.dataset.default = ''; fill(); });
      fill();
    });
  }


  /* ---------- Zakelijk (B2B) ---------- */
  function initB2B() {
    const Z = B.b2b || {};
    if (!Z.active) return;
    // Snel bestellen: aantallen invullen, alles in één keer in het mandje
    $$('[data-quick-order]').forEach((qo) => {
      if (qo.dataset.ready) return;
      qo.dataset.ready = '1';
      const countEl = $('[data-qo-count]', qo);
      const totalEl = $('[data-qo-total]', qo);
      const submit = $('[data-qo-submit]', qo);
      const rows = () => $$('[data-qo-row]', qo);
      const recalc = () => {
        let count = 0;
        let total = 0;
        rows().forEach((row) => {
          const input = $('input[type="number"]', row);
          if (!input) return;
          const n = Math.max(0, parseInt(input.value, 10) || 0);
          row.classList.toggle('has-qty', n > 0);
          count += n;
          total += n * Number(row.dataset.price || 0);
        });
        if (countEl) countEl.textContent = count;
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (submit) submit.disabled = count === 0;
      };
      qo.addEventListener('change', recalc);
      qo.addEventListener('input', recalc);
      qo.addEventListener('submit', async (e) => {
        e.preventDefault();
        const items = rows().map((row) => {
          const input = $('input[type="number"]', row);
          const n = input ? Math.max(0, parseInt(input.value, 10) || 0) : 0;
          return n > 0 ? { id: Number(row.dataset.variant), quantity: n } : null;
        }).filter(Boolean);
        if (!items.length) return;
        await Cart.addItems(items, submit);
        rows().forEach((row) => { const input = $('input[type="number"]', row); if (input) input.value = 0; });
        recalc();
      });
      recalc();
    });
  }

  /* ---------- Taal: eerste bezoek automatisch in de taal van de browser ---------- */
  function initAutoLanguage() {
    const L = B.lang;
    if (!L || !L.available || L.available.length < 2) return;
    const KEY = 'wt_lang';
    const save = (v) => { try { localStorage.setItem(KEY, v); } catch (e) { /* stil */ } };
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('button[name="language_code"]');
      if (btn && btn.value) save(btn.value);
    }, true);
    let stored = null;
    try { stored = localStorage.getItem(KEY); } catch (e) { return; }
    if (stored) return;
    if (!L.primary) { save(L.current); return; }
    if (/bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent)) return;
    const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''];
    let want = null;
    for (const item of list) {
      const code = String(item).toLowerCase().split('-')[0];
      if (L.available.includes(code)) { want = code; break; }
    }
    if (!want) want = L.available.includes('en') ? 'en' : L.current;
    save(want);
    if (want === L.current) return;
    const go = () => {
      const btn = $(`button[name="language_code"][value="${want}"]`);
      if (!btn || !btn.form) return;
      if (btn.form.requestSubmit) btn.form.requestSubmit(btn); else btn.click();
    };
    go();
  }

  /* ---------- Thema-editor ---------- */
  function initEditor() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    document.addEventListener('shopify:section:load', (e) => { initProduct(e.target); initReveal(); initHeader(); initUsp(); initPredictive(); initFacets(); initRecommendations(); initHerox(); initUtiles(); initPicker(); initChart(); initCounters(); initTabs(); initBundles(); initExplainer(); initFaq(); initGalleryTools(); });
    document.addEventListener('shopify:block:select', (e) => {
      const item = e.target.closest('.usp-bar__item');
      if (item) { $$('.usp-bar__item', item.parentElement).forEach((i) => i.classList.toggle('is-active', i === item)); }
    });
  }

  const start = () => {
    initHeader();
    initUsp();
    initDrawers();
    initCart();
    initQty();
    initProduct();
    initPredictive();
    initFacets();
    initRecommendations();
    initAddresses();
    initHerox();
    initUtiles();
    initPicker();
    initChart();
    initCounters();
    initTabs();
    initLocalization();
    initBundles();
    initExplainer();
    initFaq();
    initGalleryTools();
    initNavMotion();
    initReveal();
    initEditor();
    initB2B();
    initAutoLanguage();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.BRUUN = Object.assign(B, { formatMoney, openDrawer, closeDrawer, Cart });
})();
